/**
 * The report queue, as the notification cards want it.
 *
 * `GET /hes/api/v1/reports/downloads` is the feed behind the bell: one row per
 * report anyone has asked for, each carrying a status the server moves on its
 * own. This turns those rows into the shape `NotificationItem` draws.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  The row's field names are read through candidate lists rather than named
 *  outright. The queue's response shape isn't documented — what is known is
 *  the POST's `{ request_id, status }` and that the list carries ids — so a
 *  row that spells a field differently loses that one detail instead of
 *  rendering a blank card. Narrow these down once a real response is in hand.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** First of `keys` the row actually carries a value for. */
const pick = (row, keys) => {
  for (const key of keys) {
    const value = row?.[key]
    if (value !== undefined && value !== null && value !== "") return value
  }
  return undefined
}

/**
 * Server status -> the four states the card draws.
 *
 * Only `PENDING` is confirmed; the rest are the spellings this kind of queue
 * uses. A status nobody listed counts as still working rather than finished or
 * failed — the one reading that can't invent a file or a failure.
 */
const CARD_STATUS = {
  PENDING: "queued",
  QUEUED: "queued",
  IN_QUEUE: "queued",
  WAITING: "queued",
  PROCESSING: "queued",
  IN_PROGRESS: "queued",
  RUNNING: "queued",
  STARTED: "queued",
  SUCCESS: "downloaded",
  SUCCESSFUL: "downloaded",
  COMPLETED: "downloaded",
  COMPLETE: "downloaded",
  FINISHED: "downloaded",
  DONE: "downloaded",
  READY: "downloaded",
  FAILED: "failed",
  FAILURE: "failed",
  ERROR: "failed",
  CANCELLED: "failed",
  CANCELED: "failed",
  EXPIRED: "failed"
}

export const cardStatusOf = (status) =>
  CARD_STATUS[String(status ?? "").trim().toUpperCase()] ?? "queued"

/** Whether the server is still working on it — what drives the polling. */
export const isPending = (item) => item?.file?.status === "queued"

/** The titles the HES tabs give their reports, so the card reads the same. */
const REPORT_TITLES = {
  command_history: "Command History Export",
  periodic_push: "Push Data Export",
  latest_data: "Meter Profile Export",
  high_move_in_read: "High Move In Read Report"
}

/** "periodic_push" -> "Periodic Push", for a report with no title of its own. */
export const titleForReport = (name) =>
  REPORT_TITLES[name] ??
  String(name ?? "Report")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()

/** "report.xlsx" from the report name, when the row doesn't name the file. */
const fileNameFor = (row, reportName) =>
  pick(row, ["file_name", "filename", "file", "object_name"]) ??
  `${reportName || "report"}.xlsx`

const sizeLabel = (value) => {
  const bytes = Number(value)
  if (!Number.isFinite(bytes) || bytes <= 0) return undefined
  if (bytes < 1024) return `${Math.round(bytes)} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const minutesSince = (value, now) => {
  const at = value ? Date.parse(value) : NaN
  if (Number.isNaN(at)) return 0
  return Math.max(0, Math.floor((now - at) / 60000))
}

/** The request id, which is how every other call refers to this row. */
export const requestIdOf = (row) => pick(row, ["request_id", "id", "download_id", "report_id"])

/**
 * One queue row as a notification.
 *
 * `project` is passed in rather than read off the row: the endpoint is scoped
 * by the `project` header, so every row belongs to the project whose screen
 * fetched it, and the row itself needn't repeat it.
 */
export const toNotification = (row, { project, now = Date.now() } = {}) => {
  const reportName = pick(row, ["report_name", "report", "name"])
  const status = pick(row, ["status", "state"])
  const createdAt = pick(row, ["created_at", "requested_at", "created", "created_on"])
  const updatedAt = pick(row, ["updated_at", "completed_at", "finished_at", "modified_at"])
  const cardStatus = cardStatusOf(status)

  return {
    id: String(requestIdOf(row) ?? `${reportName}-${createdAt}`),
    requestId: requestIdOf(row),
    reportName,
    project: pick(row, ["project"]) ?? project,
    title: titleForReport(reportName),
    // The design's mark: the failed ones get the error glyph.
    tone: cardStatus === "failed" ? "error" : "success",
    // Nothing in the queue says whether anyone has looked at it, and a card
    // that claimed to know would be inventing the one thing the accent down
    // its edge is for. Every row reads as unread.
    read: false,
    minutesAgo: minutesSince(updatedAt ?? createdAt, now),
    request: {
      user: pick(row, ["username", "user", "requested_by", "created_by", "user_name"]),
      at: createdAt ? Date.parse(createdAt) : undefined
    },
    // Whatever the row says it asked for, so a failed one can be asked again.
    filters: pick(row, ["filters", "params", "request_params", "payload"]),
    file: {
      name: fileNameFor(row, reportName),
      status: cardStatus,
      size: sizeLabel(pick(row, ["file_size", "size", "size_bytes", "bytes"])),
      reason:
        cardStatus === "failed"
          ? pick(row, ["error_message", "error", "reason", "message", "remarks"])
          : undefined
    }
  }
}

/**
 * The queue response as a list of notifications, newest first.
 *
 * The envelope has been every shape on this API — a bare list, `data`, or
 * `data.items` — so all three are read rather than one being assumed.
 */
export const toNotifications = (response, { project, now = Date.now() } = {}) => {
  const body = response?.data
  const payload = body?.data ?? body
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.results)
    ? payload.results
    : []

  return rows
    .map((row) => toNotification(row, { project, now }))
    .sort((a, b) => a.minutesAgo - b.minutesAgo)
}

/**
 * The link a finished report can be collected from.
 *
 * Read through candidates for the same reason as the rest of this file: the
 * endpoint's envelope isn't documented, and a link under an unexpected key
 * should be a missing link rather than a crash.
 */
export const linkFrom = (response) => {
  const body = response?.data
  const payload = body?.data ?? body
  return (
    pick(payload, ["link", "file_url", "url", "download_link", "download_url", "signed_url"]) ??
    null
  )
}
